import { useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import _ from 'lodash';

import {
    TransferList,
    TransferListItem,
    TransferListProps,
} from '../shared/TransferList';
import { userToQuery, UserType } from './UserType';

import { useGiftApi } from '../hooks/useGiftApi';

interface GiftV2ManagementProps {
    user: UserType;
}

const useLegacyData = (user: UserType) => {
    const giftApi = useGiftApi();
    const allGiftsQuery = giftApi.useQuery('get', '/gift/api/v1/admin/gifts');
    const giftClaimsQuery = giftApi.useQuery(
        'get',
        '/gift/api/v1/admin/gifts/claim',
        {
            params: {
                query: userToQuery(user),
            },
        },
    );
    const updateClaimsMutation = giftApi.useMutation(
        'put',
        '/gift/api/v1/admin/gifts/claim',
    );

    const giftLabelMap = useMemo(() => {
        if (!allGiftsQuery.data) return {};
        return _.mapValues(_.keyBy(allGiftsQuery.data, 'giftId'), 'giftId');
    }, [allGiftsQuery.data]);
    const allGiftIds = useMemo(() => {
        if (!allGiftsQuery.data) return new Set<string>();
        return new Set(allGiftsQuery.data.map((gift) => gift.giftId));
    }, [allGiftsQuery.data]);

    const claimedGiftIds = useMemo(() => {
        if (!giftClaimsQuery.data) return new Set<string>();
        return new Set(giftClaimsQuery.data.gifts.map((gift) => gift.giftId));
    }, [giftClaimsQuery.data]);

    const availableGiftIds = useMemo(() => {
        return allGiftIds.difference(claimedGiftIds);
    }, [allGiftIds, claimedGiftIds]);

    return {
        giftClaimsQuery,
        updateClaimsMutation,
        giftLabelMap,
        allGiftIds,
        availableGiftIds,
        claimedGiftIds,
    };
};

const useV2Data = (user: UserType) => {
    const giftApi = useGiftApi();
    const allGiftsQuery = giftApi.useQuery('get', '/gift/api/v2/admin/gifts');
    const giftClaimsQuery = giftApi.useQuery(
        'get',
        '/gift/api/v2/admin/gifts/claim',
        {
            params: {
                query: userToQuery(user),
            },
        },
    );
    const updateClaimsMutation = giftApi.useMutation(
        'put',
        '/gift/api/v2/admin/gifts/claim',
    );

    const giftLabelMap = useMemo(() => {
        if (!allGiftsQuery.data) return {};
        return _.mapValues(_.keyBy(allGiftsQuery.data.gifts, 'id'), 'name');
    }, [allGiftsQuery.data]);
    const allGiftIds = useMemo(() => {
        if (!allGiftsQuery.data) return new Set<string>();
        return new Set(allGiftsQuery.data.gifts.map((gift) => gift.id));
    }, [allGiftsQuery.data]);
    const claimedGiftIds = useMemo(() => {
        if (!giftClaimsQuery.data) return new Set<string>();
        return new Set(giftClaimsQuery.data.gifts.map((gift) => gift.id));
    }, [giftClaimsQuery.data]);

    const availableGiftIds = useMemo(() => {
        return allGiftIds.difference(claimedGiftIds);
    }, [allGiftIds, claimedGiftIds]);

    return {
        giftClaimsQuery,
        updateClaimsMutation,
        giftLabelMap,
        availableGiftIds,
        claimedGiftIds,
    };
};

export const GiftManagement = ({ user }: GiftV2ManagementProps) => {
    const legacyData = useLegacyData(user);
    const v2Data = useV2Data(user);

    const isV2Loading =
        v2Data.giftClaimsQuery.isPending ||
        v2Data.updateClaimsMutation.isPending;
    const isLegacyLoading =
        legacyData.giftClaimsQuery.isPending ||
        legacyData.updateClaimsMutation.isPending;

    const createHandleOnChange = useCallback(
        (
            mutation: (
                | typeof legacyData
                | typeof v2Data
            )['updateClaimsMutation'],
            query: (typeof legacyData | typeof v2Data)['giftClaimsQuery'],
        ) => {
            return (
                _: TransferListItem[],
                newClaimedItems: TransferListItem[],
            ) => {
                mutation.mutate(
                    {
                        body: {
                            user,
                            giftIds: newClaimedItems.map((item) => item.id),
                        },
                    },
                    {
                        onSettled: () => {
                            query.refetch();
                        },
                    },
                );
            };
        },
        [user],
    );

    const handleV2Change = useMemo(
        () =>
            createHandleOnChange(
                v2Data.updateClaimsMutation,
                v2Data.giftClaimsQuery,
            ),
        [
            createHandleOnChange,
            v2Data.giftClaimsQuery,
            v2Data.updateClaimsMutation,
        ],
    );

    const handleLegacyChange = useMemo(
        () =>
            createHandleOnChange(
                legacyData.updateClaimsMutation,
                legacyData.giftClaimsQuery,
            ),
        [
            createHandleOnChange,
            legacyData.giftClaimsQuery,
            legacyData.updateClaimsMutation,
        ],
    );

    return (
        <Box>
            <Box>
                <Typography variant="h6">Legacy Gifts</Typography>
                <GiftTransferList
                    availableGiftIds={legacyData.availableGiftIds}
                    claimedGiftIds={legacyData.claimedGiftIds}
                    giftLabelMap={legacyData.giftLabelMap}
                    onChange={handleLegacyChange}
                    isLoading={isLegacyLoading}
                />
            </Box>
            <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Gifts V2</Typography>
                <GiftTransferList
                    availableGiftIds={v2Data.availableGiftIds}
                    claimedGiftIds={v2Data.claimedGiftIds}
                    giftLabelMap={v2Data.giftLabelMap}
                    onChange={handleV2Change}
                    isLoading={isV2Loading}
                />
            </Box>
        </Box>
    );
};

interface GiftTransferListProps {
    availableGiftIds: Set<string>;
    claimedGiftIds: Set<string>;
    giftLabelMap: Record<string, string>;
    onChange: TransferListProps['onChange'];
    isLoading: boolean;
}
const GiftTransferList = ({
    availableGiftIds,
    claimedGiftIds,
    giftLabelMap,
    onChange,
    isLoading,
}: GiftTransferListProps) => {
    const availableItems = useMemo(() => {
        return Array.from(availableGiftIds).map((giftId) => {
            return {
                id: giftId,
                label: giftLabelMap[giftId],
            } satisfies TransferListItem;
        });
    }, [availableGiftIds, giftLabelMap]);

    const claimedItems = useMemo(() => {
        return Array.from(claimedGiftIds).map((giftId) => {
            return {
                id: giftId,
                label: giftLabelMap[giftId],
            } satisfies TransferListItem;
        });
    }, [claimedGiftIds, giftLabelMap]);

    return (
        <TransferList
            left={availableItems}
            right={claimedItems}
            leftTitle="Available Gifts"
            rightTitle="Claimed Gifts"
            onChange={onChange}
            isLoading={isLoading}
        />
    );
};
